import { headerIndex, normalizeHeader, parseCsv, type CsvRow } from './parse-csv.js';
import { isCurrency, type Currency, type InvestmentTransactionType } from '../types/enums.js';
import { isIsoDate } from '../dates/index.js';
import type { DecimalString, IsoDate } from '../types/domain.js';

/**
 * Lightyear account-statement importer.
 *
 * Lightyear has no retail API, so activity arrives as a CSV the user exports
 * by hand. The format is not contractual, so:
 *
 *  - the parser is versioned, and every import records which version read it;
 *  - column names are matched through an alias table rather than by position;
 *  - a bad row is collected as an error instead of aborting the file, so the
 *    UI can show "412 of 415 rows ready, 3 need attention".
 *
 * Version 2 is written against a real export (`AccountStatement-LY-…_hu.csv`,
 * 2024-02 to 2026-09). Version 1 was a guess and got several things wrong —
 * see D-21. The fixtures in the test file preserve that export's structure.
 */
export const LIGHTYEAR_PARSER_VERSION = 2;

/**
 * Header aliases, normalised (trimmed, lowercased, whitespace collapsed).
 * Punctuation survives normalisation, so `Net Amt.` is matched with its dot.
 */
const COLUMN_ALIASES = {
  date: ['date', 'trade date', 'executed at', 'transaction date'],
  type: ['type', 'transaction type', 'direction', 'side'],
  ticker: ['ticker', 'symbol', 'instrument'],
  isin: ['isin'],
  quantity: ['quantity', 'shares', 'units', 'no. of shares'],
  price: ['price/share', 'price per share', 'price', 'share price', 'unit price'],
  grossAmount: ['gross amount', 'gross amt.', 'gross', 'amount', 'total'],
  netAmount: ['net amt.', 'net amount', 'net'],
  currency: ['ccy', 'currency', 'instrument currency'],
  fee: ['fee', 'fees', 'commission'],
  tax: ['tax amt.', 'tax amount', 'tax'],
  externalId: ['reference', 'id', 'transaction id', 'order id'],
} as const satisfies Record<string, readonly string[]>;

type ColumnName = keyof typeof COLUMN_ALIASES;

const REQUIRED_COLUMNS: readonly ColumnName[] = ['date', 'type', 'currency'];

/** Statement types that become ledger rows. */
const TYPE_MAP: Record<string, InvestmentTransactionType> = {
  buy: 'buy',
  bought: 'buy',
  purchase: 'buy',
  sell: 'sell',
  sold: 'sell',
  sale: 'sell',
  // Lightyear calls every income payment a Distribution, including the
  // monthly interest on its money-market cash funds. The CSV gives us no way
  // to tell the two apart; `instruments.asset_class` is where that is fixed.
  distribution: 'dividend',
  dividend: 'dividend',
  'dividend payment': 'dividend',
  interest: 'interest',
  fee: 'fee',
  'transaction fee': 'fee',
  tax: 'tax',
  'withholding tax': 'tax',
};

/**
 * Real rows that are deliberately not ledger entries.
 *
 * `investment_transaction_type` has no member for moving cash in, out or
 * between currencies, and inventing one would put rows in the ledger that
 * `buildPositions` has to skip anyway. They are reported rather than dropped,
 * so an import can say what it left behind.
 */
const CASH_MOVEMENTS: Record<string, string> = {
  conversion: 'devizaváltás',
  deposit: 'befizetés',
  withdrawal: 'kifizetés',
  'cash top-up': 'befizetés',
};

export interface LightyearTransaction {
  tradeDate: IsoDate;
  type: InvestmentTransactionType;
  ticker: string | null;
  isin: string | null;
  quantity: DecimalString | null;
  price: DecimalString | null;
  /** Signed cash effect, unsigned here; `type` carries the direction. */
  amount: DecimalString;
  currency: Currency;
  /** Stable key for the idempotent upsert. Falls back to a content hash. */
  externalId: string;
}

export interface RowError {
  /** 1-based line number in the file, counting the header. */
  line: number;
  message: string;
  raw: CsvRow;
}

/** A row the parser understood and chose not to import. */
export interface SkippedRow {
  line: number;
  type: string;
  reason: string;
}

/** Which way round the file writes `01/09/2026`. */
export type DateOrder = 'day-first' | 'month-first';

export interface LightyearParseResult {
  parserVersion: number;
  /** Inferred from the data, not assumed. */
  dateOrder: DateOrder;
  /**
   * False when no row carried a component above 12, leaving 01/09 genuinely
   * ambiguous. The import UI should say so rather than pretend.
   */
  dateOrderCertain: boolean;
  transactions: LightyearTransaction[];
  skipped: SkippedRow[];
  errors: RowError[];
}

export class LightyearFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LightyearFormatError';
  }
}

export function parseLightyearCsv(input: string): LightyearParseResult {
  const rows = parseCsv(input).filter((row) => row.some((cell) => cell.trim() !== ''));
  const header = rows[0];
  if (!header) throw new LightyearFormatError('A fájl üres.');

  const columns = resolveColumns(header);
  const missing = REQUIRED_COLUMNS.filter((name) => columns[name] === undefined);
  if (missing.length > 0) {
    throw new LightyearFormatError(
      `Missing required column(s): ${missing.join(', ')}. Found: ${header.join(', ')}`,
    );
  }
  if (columns.grossAmount === undefined && columns.netAmount === undefined) {
    throw new LightyearFormatError(
      `No amount column. Expected one of: ${[...COLUMN_ALIASES.grossAmount, ...COLUMN_ALIASES.netAmount].join(', ')}. Found: ${header.join(', ')}`,
    );
  }

  const body = rows.slice(1);
  const { order: dateOrder, certain: dateOrderCertain } = inferDateOrder(
    body.map((raw) => cell(raw, columns, 'date')),
  );

  // A conversion is two rows sharing one Reference. Nothing we keep collides
  // today, but `(account_id, external_id)` is a unique index, so a collision
  // would reject a row rather than report one.
  const referenceCounts = new Map<string, number>();
  for (const raw of body) {
    const reference = cell(raw, columns, 'externalId');
    if (reference !== '') {
      referenceCounts.set(reference, (referenceCounts.get(reference) ?? 0) + 1);
    }
  }

  const transactions: LightyearTransaction[] = [];
  const skipped: SkippedRow[] = [];
  const errors: RowError[] = [];

  body.forEach((raw, index) => {
    const line = index + 2;
    try {
      const parsed = parseRow(raw, columns, dateOrder, referenceCounts);
      if (parsed.kind === 'skipped') skipped.push({ line, ...parsed.detail });
      else transactions.push(...parsed.transactions);
    } catch (error) {
      errors.push({
        line,
        message: error instanceof Error ? error.message : String(error),
        raw,
      });
    }
  });

  return {
    parserVersion: LIGHTYEAR_PARSER_VERSION,
    dateOrder,
    dateOrderCertain,
    transactions,
    skipped,
    errors,
  };
}

type ResolvedColumns = Partial<Record<ColumnName, number>>;

function resolveColumns(header: CsvRow): ResolvedColumns {
  const index = headerIndex(header);
  const resolved: ResolvedColumns = {};

  for (const [name, aliases] of Object.entries(COLUMN_ALIASES) as Array<
    [ColumnName, readonly string[]]
  >) {
    for (const alias of aliases) {
      const position = index.get(normalizeHeader(alias));
      if (position !== undefined) {
        resolved[name] = position;
        break;
      }
    }
  }

  return resolved;
}

const SLASHED_DATE = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/;

/**
 * Work out whether `01/09/2026` means 1 September or 9 January.
 *
 * Guessing wrong silently shuffles a year of trades into the wrong months and
 * nothing downstream would notice, so this reads the answer off the file: any
 * component above 12 settles it. Rows that disagree are a format error, not a
 * row error — the whole file is then untrustworthy.
 */
export function inferDateOrder(values: readonly string[]): {
  order: DateOrder;
  certain: boolean;
} {
  let dayFirst = false;
  let monthFirst = false;

  for (const value of values) {
    const parts = SLASHED_DATE.exec(datePart(value));
    if (!parts) continue;

    const first = Number(parts[1]);
    const second = Number(parts[2]);
    if (first > 12) dayFirst = true;
    if (second > 12) monthFirst = true;
  }

  if (dayFirst && monthFirst) {
    throw new LightyearFormatError(
      'The file mixes DD/MM/YYYY and MM/DD/YYYY dates. Re-export it without ' +
        'opening it in a spreadsheet, which rewrites dates to the local format.',
    );
  }

  return { order: monthFirst ? 'month-first' : 'day-first', certain: dayFirst || monthFirst };
}

function datePart(value: string): string {
  return value.trim().split(/[ T]/)[0] ?? '';
}

type ParsedRow =
  | { kind: 'transactions'; transactions: LightyearTransaction[] }
  | { kind: 'skipped'; detail: { type: string; reason: string } };

function parseRow(
  raw: CsvRow,
  columns: ResolvedColumns,
  dateOrder: DateOrder,
  referenceCounts: ReadonlyMap<string, number>,
): ParsedRow {
  const rawType = required(raw, columns, 'type');
  const tradeDate = normalizeDate(required(raw, columns, 'date'), dateOrder);

  const currencyText = required(raw, columns, 'currency').toUpperCase();
  if (!isCurrency(currencyText)) {
    throw new Error(`Unsupported currency: ${currencyText}`);
  }

  const reference = blankToNull(cell(raw, columns, 'externalId'));
  const fee = optionalDecimal(cell(raw, columns, 'fee'));
  const cashMovement = CASH_MOVEMENTS[rawType.trim().toLowerCase()];

  if (cashMovement !== undefined) {
    // The conversion itself is not a holding, but its fee is real money — the
    // FX charge on a large transfer is not small — and nothing else in the
    // file records it.
    const feeRow =
      fee !== null && !isZero(fee)
        ? [
            {
              tradeDate,
              type: 'fee' as const,
              ticker: null,
              isin: null,
              quantity: null,
              price: null,
              amount: fee,
              currency: currencyText,
              externalId: `${reference ?? syntheticId({ tradeDate, type: rawType, ticker: null, isin: null, quantity: null, amount: fee })}|fee|${currencyText}`,
            },
          ]
        : [];

    if (feeRow.length > 0) return { kind: 'transactions', transactions: feeRow };
    return { kind: 'skipped', detail: { type: rawType, reason: cashMovement } };
  }

  const type = normalizeType(rawType);
  const gross = optionalDecimal(cell(raw, columns, 'grossAmount'));
  const net = optionalDecimal(cell(raw, columns, 'netAmount'));
  const amount = amountFor(type, gross, net);

  const quantity = optionalDecimal(cell(raw, columns, 'quantity'));
  const price = optionalDecimal(cell(raw, columns, 'price'));
  const ticker = blankToNull(cell(raw, columns, 'ticker'));
  const isin = blankToNull(cell(raw, columns, 'isin'));

  const duplicated = reference !== null && (referenceCounts.get(reference) ?? 0) > 1;
  const externalId =
    reference === null
      ? syntheticId({ tradeDate, type, ticker, isin, quantity, amount })
      : duplicated
        ? `${reference}|${currencyText}`
        : reference;

  return {
    kind: 'transactions',
    transactions: [
      {
        tradeDate,
        type,
        ticker,
        isin,
        quantity,
        price,
        amount,
        currency: currencyText,
        externalId,
      },
    ],
  };
}

/**
 * Which amount column the ledger wants, which is not the same one both ways.
 *
 * On a buy the fee is part of what the position cost you, and Gross is the
 * full debit — `quantity × price` equals Net. On a sell the fee comes out of
 * what you received, so Net is the proceeds and `quantity × price` equals
 * Gross. `buildPositions` adds `|amount|` to the cost basis on a buy and
 * treats it as proceeds on a sell, so each side needs the other column.
 */
function amountFor(
  type: InvestmentTransactionType,
  gross: DecimalString | null,
  net: DecimalString | null,
): DecimalString {
  const chosen = type === 'buy' ? (gross ?? net) : (net ?? gross);
  if (chosen === null) throw new Error('Row has neither a gross nor a net amount');
  return chosen;
}

function isZero(value: DecimalString): boolean {
  return /^-?0*(\.0*)?$/.test(value);
}

function cell(raw: CsvRow, columns: ResolvedColumns, name: ColumnName): string {
  const position = columns[name];
  if (position === undefined) return '';
  return raw[position]?.trim() ?? '';
}

function required(raw: CsvRow, columns: ResolvedColumns, name: ColumnName): string {
  const value = cell(raw, columns, name);
  if (value === '') throw new Error(`Empty required column: ${name}`);
  return value;
}

function blankToNull(value: string): string | null {
  return value === '' ? null : value;
}

/** Accept ISO and slash/dot dates, reading the component order from the file. */
export function normalizeDate(value: string, order: DateOrder = 'day-first'): IsoDate {
  const trimmed = datePart(value);
  if (isIsoDate(trimmed)) return trimmed;

  const parts = SLASHED_DATE.exec(trimmed);
  if (parts) {
    const [, first, second, year] = parts;
    const day = order === 'day-first' ? first! : second!;
    const month = order === 'day-first' ? second! : first!;
    const candidate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    if (isIsoDate(candidate)) return candidate;
  }

  throw new Error(`Unrecognised date: ${value}`);
}

function normalizeType(value: string): InvestmentTransactionType {
  const mapped = TYPE_MAP[value.trim().toLowerCase()];
  if (!mapped) throw new Error(`Unrecognised transaction type: ${value}`);
  return mapped;
}

/**
 * Turn a locale-formatted number into a plain decimal string.
 * Handles `1,234.56`, `1 234,56` and `1.234,56` without going through `Number`.
 */
export function normalizeDecimal(value: string): DecimalString {
  const cleaned = value.replace(/[\s\u00a0]/g, '').replace(/[^\d,.-]/g, '');
  if (cleaned === '' || cleaned === '-') throw new Error(`Not a number: ${value}`);

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  let normalized: string;

  if (lastComma === -1 && lastDot === -1) {
    normalized = cleaned;
  } else if (lastComma > lastDot) {
    // Comma is the decimal separator: 1.234,56
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // Dot is the decimal separator: 1,234.56
    normalized = cleaned.replace(/,/g, '');
  }

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) throw new Error(`Not a number: ${value}`);
  return normalized;
}

function optionalDecimal(value: string): DecimalString | null {
  return value === '' ? null : normalizeDecimal(value);
}

/**
 * When the export carries no transaction id, derive a stable one from the row
 * content so a re-import upserts instead of duplicating.
 */
export function syntheticId(parts: {
  tradeDate: string;
  type: string;
  ticker: string | null;
  isin: string | null;
  quantity: string | null;
  amount: string;
}): string {
  const payload = [
    parts.tradeDate,
    parts.type,
    parts.ticker ?? '',
    parts.isin ?? '',
    parts.quantity ?? '',
    parts.amount,
  ].join('|');
  return `ly:v${LIGHTYEAR_PARSER_VERSION}:${fnv1a32(payload)}`;
}

/** FNV-1a — not cryptographic, just a short stable digest for dedupe keys. */
function fnv1a32(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
